import { browser } from '$app/environment';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogMessage {
  message: string;
  context?: Record<string, unknown>;
}

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

const SENSITIVE_KEYS = new Set([
  'token',
  'access_token',
  'refresh_token',
  'authorization',
  'auth',
  'password',
  'email',
]);

function redactedValue(value: unknown): unknown {
  if (typeof value === 'string') {
    if (value.length <= 8) {
      return '***';
    }
    return `${value.slice(0, 4)}…${value.slice(-4)}`;
  }
  if (typeof value === 'object' && value !== null) {
    return '***';
  }
  return '***';
}

function normalizeContext(context: unknown): Record<string, unknown> | undefined {
  if (context === undefined || context === null) return undefined;
  if (context instanceof Error) {
    return { error: context.message, stack: context.stack };
  }
  if (typeof context === 'object' && !Array.isArray(context)) {
    return context as Record<string, unknown>;
  }
  return { value: context };
}

function sanitizeContext(
  context: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!context) return context;

  return Object.fromEntries(
    Object.entries(context).map(([key, value]) => {
      if (SENSITIVE_KEYS.has(key.toLowerCase())) {
        return [key, redactedValue(value)];
      }
      return [key, value];
    })
  );
}

function getConfiguredLevel(): LogLevel {
  const explicitLevel = (import.meta.env.VITE_LOG_LEVEL || '').toLowerCase();

  if (explicitLevel && explicitLevel in LEVEL_ORDER) {
    return explicitLevel as LogLevel;
  }

  return import.meta.env.DEV ? 'debug' : 'warn';
}

const enableDebug = (import.meta.env.VITE_DEBUG_LOGS || '').toLowerCase() === 'true';
const effectiveLevel = enableDebug ? 'debug' : getConfiguredLevel();

function shouldLog(level: LogLevel): boolean {
  const target = LEVEL_ORDER[level];
  const threshold = LEVEL_ORDER[effectiveLevel];

  if (target < threshold) {
    return false;
  }

  if (!browser && !import.meta.env.DEV && level === 'debug') {
    return false;
  }

  return true;
}

function log(level: LogLevel, details: LogMessage) {
  if (!shouldLog(level)) return;

  const sanitizedContext = sanitizeContext(details.context);
  const payload = sanitizedContext ? [details.message, sanitizedContext] : [details.message];

  // eslint-disable-next-line no-console
  console[level === 'debug' ? 'log' : level](...payload);
}

export const logger = {
  debug(message: string, context?: unknown) {
    log('debug', { message, context: normalizeContext(context) });
  },
  info(message: string, context?: unknown) {
    log('info', { message, context: normalizeContext(context) });
  },
  warn(message: string, context?: unknown) {
    log('warn', { message, context: normalizeContext(context) });
  },
  error(message: string, context?: unknown) {
    log('error', { message, context: normalizeContext(context) });
  },
};

export default logger;
